import { num, nullableStr, oneOf, record, records, str, strList } from '@/features/brandHub/fields';

export const BRAND_STATES = ['draft', 'ready'] as const;
export type BrandState = (typeof BRAND_STATES)[number];

/** The four mandatory materials, in the order the readiness guide lists them. */
export const MATERIALS = ['logos', 'colours', 'font', 'guidelines'] as const;
export type Material = (typeof MATERIALS)[number];

export type BrandChecklist = Record<Material, boolean>;

/** One palette entry, as CMYK percentages. */
export interface BrandColour {
  label: string;
  cyan: number;
  magenta: number;
  yellow: number;
  key: number;
}

export const CMYK_CHANNELS = ['cyan', 'magenta', 'yellow', 'key'] as const;

/** Whether two colours print the same, whatever either one is called. */
export function sameShade(left: BrandColour, right: BrandColour): boolean {
  return CMYK_CHANNELS.every((channel) => left[channel] === right[channel]);
}

export const FONT_SOURCES = ['upload', 'google'] as const;
export type FontSource = (typeof FONT_SOURCES)[number];

/** One font of the brand. Its file is always ours, uploaded or copied from Google Fonts. */
export interface BrandFont {
  fontId: string;
  family: string;
  objectName: string;
  source: FontSource;
}

/** What the generation service writes onto the brand while it reads the guidelines. */
export const EXTRACTION_STATUSES = ['queued', 'in_progress', 'completed', 'failed'] as const;
export type ExtractionStatus = (typeof EXTRACTION_STATUSES)[number];

export interface Brand {
  id: string;
  name: string;
  state: BrandState;
  checklist: BrandChecklist;
  colours: BrandColour[];
  fonts: BrandFont[];
  tags: string[];
  /** A deactivated brand keeps its documents but leaves the hub. */
  isActive: boolean;
  extractionStatus: ExtractionStatus | null;
  /** Colours proposed from the guidelines, kept apart from the palette until added. */
  extractedColours: BrandColour[];
}

function percent(value: unknown): number {
  return Math.min(100, Math.max(0, Math.round(num(value))));
}

function colours(value: unknown): BrandColour[] {
  return records(value)
    .filter((item) => typeof item.label === 'string' && item.label)
    .map((item) => ({
      label: str(item.label),
      cyan: percent(item.cyan),
      magenta: percent(item.magenta),
      yellow: percent(item.yellow),
      key: percent(item.key),
    }));
}

/** Every well-formed entry of the fonts list; one without an id, a family or a file is skipped. */
function fonts(value: unknown): BrandFont[] {
  return records(value)
    .filter((item) => nullableStr(item.font_id) && nullableStr(item.family) && nullableStr(item.object_name))
    .map((item) => ({
      fontId: str(item.font_id),
      family: str(item.family),
      objectName: str(item.object_name),
      source: oneOf(FONT_SOURCES, item.source, 'upload'),
    }));
}

/** Reads a brand document. One with no name is dropped. */
export function parseBrand(id: string, raw: Record<string, unknown>): Brand | null {
  const name = str(raw.name);
  if (!name) return null;
  const checklist = record(raw.checklist);
  return {
    id,
    name,
    state: oneOf(BRAND_STATES, raw.state, 'draft'),
    checklist: {
      logos: checklist.logos === true,
      colours: checklist.colours === true,
      font: checklist.font === true,
      guidelines: checklist.guidelines === true,
    },
    colours: colours(raw.colours),
    fonts: fonts(raw.fonts),
    tags: strList(raw.tags),
    isActive: raw.is_active !== false,
    extractionStatus: EXTRACTION_STATUSES.find((status) => status === raw.status) ?? null,
    extractedColours: colours(raw.extracted_colours),
  };
}

/** How many of the four mandatory materials a brand carries. */
export function materialsDone(brand: Brand): number {
  return MATERIALS.filter((material) => brand.checklist[material]).length;
}

/** The hex a swatch paints for one CMYK colour. */
export function cmykToHex(colour: BrandColour): string {
  const channel = (value: number) =>
    Math.round(255 * (1 - value / 100) * (1 - colour.key / 100))
      .toString(16)
      .padStart(2, '0');
  return `#${channel(colour.cyan)}${channel(colour.magenta)}${channel(colour.yellow)}`;
}

/** The gradient a brand's palette paints, or nothing while it has no colours. */
export function paletteGradient(brand: Brand): string | undefined {
  if (brand.colours.length === 0) return undefined;
  const stops = brand.colours.slice(0, 4).map(cmykToHex);
  return `linear-gradient(135deg, ${(stops.length > 1 ? stops : [...stops, ...stops]).join(', ')})`;
}
